<template>
  <div class="page-outer halls-list-page">
    <div class="page-header page-header-halls-list">
      <div class="header-left">
        <h1 class="page-title"><i class="ri-layout-column-line"></i> 館別管理</h1>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-primary" @click="goToCreate">
          <i class="ri-add-line"></i> 新增館別
        </button>
      </div>
    </div>

    <div v-if="loading" class="loading-state"><div class="spinner"></div><p>載入中...</p></div>
    <div v-else-if="error" class="error-state">{{ error }}</div>
    <div v-else class="page-container">
      <div v-if="!halls.length" class="empty-state">
        <i class="ri-layout-column-line empty-icon"></i>
        <p>尚無館別，請新增第一個館別。</p>
      </div>
      <div v-else class="halls-grid">
        <div v-for="hall in halls" :key="hall.id" class="hall-card">
          <div class="hall-card-cover" :style="hall.cover_image_url ? `background-image: url('${hall.cover_image_url}')` : ''">
            <div v-if="!hall.cover_image_url" class="hall-card-cover-placeholder"><i class="ri-image-line"></i></div>
            <span class="hall-status-badge" :class="hall.is_active ? 'active' : 'inactive'">
              {{ hall.is_active ? '上架' : '下架' }}
            </span>
          </div>
          <div class="hall-card-body">
            <div class="hall-card-name">{{ hall.name }}</div>
            <div class="hall-card-slug">{{ hall.slug }}</div>
            <div class="hall-card-product-count">
              <i class="ri-shopping-bag-3-line"></i> 商品 {{ hallProductCount(hall) }} 項
            </div>
            <div class="hall-card-actions">
              <button class="btn btn-secondary btn-sm" @click="goToEdit(hall)"><i class="ri-edit-line"></i> 編輯</button>
              <button class="btn btn-secondary btn-sm" @click="goToProducts(hall)"><i class="ri-list-check"></i> 編輯商品</button>
              <button class="btn btn-danger btn-sm" @click="confirmDelete(hall)"><i class="ri-delete-bin-line"></i></button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { usePageTitle } from '@/composables/usePageTitle.js'
import { commerceSettingsApi } from '@/api/modules/commerceSettings.js'
import { showError, showSuccess, showDeleteConfirm } from '@/utils/swal.js'

const router = useRouter()

usePageTitle('館別管理')

const halls = ref([])
const loading = ref(false)
const error = ref('')

function goToCreate() {
  router.push('/client/commerce/halls/create')
}

function goToEdit(hall) {
  router.push(`/client/commerce/halls/${encodeURIComponent(hall.id)}/edit`)
}

function goToProducts(hall) {
  router.push(`/client/commerce/halls/${encodeURIComponent(hall.id)}/products`)
}

function hallProductCount(hall) {
  const n = hall?.product_count ?? hall?.productCount
  if (typeof n === 'number' && !Number.isNaN(n)) return n
  const parsed = Number.parseInt(String(n ?? ''), 10)
  return Number.isNaN(parsed) ? 0 : parsed
}

async function loadHalls() {
  loading.value = true
  error.value = ''
  try {
    const res = await commerceSettingsApi.listHalls()
    halls.value = res?.data?.items ?? []
  } catch (e) {
    error.value = e?.response?.data?.message || '載入館別失敗'
  } finally {
    loading.value = false
  }
}

async function confirmDelete(hall) {
  const confirmed = await showDeleteConfirm(`確定刪除「${hall.name}」？`)
  if (!confirmed) return
  try {
    await commerceSettingsApi.deleteHall(hall.id)
    await showSuccess('館別已刪除')
    await loadHalls()
  } catch (e) {
    await showError(e?.response?.data?.message || '刪除失敗')
  }
}

onMounted(loadHalls)
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.page-outer {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

.page-header {
  margin-bottom: 0;
  background: #f5f7fa;
  border-bottom: 1px solid #e5e7eb;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  padding: 10px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.header-actions { display: flex; align-items: center; gap: 8px; }

.page-header .page-title {
  font-size: 20px;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.page-container {
  padding: 20px;
  padding-top: 72px;
}

.halls-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 16px;
  padding: 4px 0;
}

.hall-card {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 1px 4px rgba(0,0,0,.06);
  transition: box-shadow 0.2s;
}
.hall-card:hover { box-shadow: 0 4px 12px rgba(0,0,0,.1); }

.hall-card-cover {
  position: relative;
  width: 100%;
  height: 130px;
  background: #f3f4f6 center/cover no-repeat;
}
.hall-card-cover-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  font-size: 28px;
  color: #d1d5db;
}
.hall-status-badge {
  position: absolute;
  top: 8px;
  right: 8px;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
}
.hall-status-badge.active { background: #d1fae5; color: #065f46; }
.hall-status-badge.inactive { background: #f3f4f6; color: #6b7280; }

.hall-card-body { padding: 12px; }
.hall-card-name { font-size: 15px; font-weight: 600; color: #111827; margin-bottom: 2px; }
.hall-card-slug { font-size: 12px; color: #9ca3af; margin-bottom: 6px; }
.hall-card-product-count {
  font-size: 13px;
  color: #4b5563;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 4px;
}
.hall-card-product-count i { font-size: 15px; color: #6b7280; }
.hall-card-actions { display: flex; gap: 6px; flex-wrap: wrap; }
</style>

<style>
.page-header-halls-list {
  position: fixed;
  top: 56px;
  left: 260px;
  right: 0;
  z-index: 100;
}
.sidebar.collapsed ~ .main-content .page-header-halls-list {
  left: 64px;
}
@media (max-width: 768px) {
  .page-header-halls-list {
    left: 0 !important;
    top: 56px;
  }
}
</style>
